// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { SvgIconName } from '@shared/ui-kit/components';

export type SuitabilityProfileType =
  | 'conservative'
  | 'moderate'
  | 'dynamic'
  | 'aggressive';

export interface SuitabilityProfileMetadata {
  type: SuitabilityProfileType;
  icon: SvgIconName;
  iconColor: 'main' | 'success' | 'secondary' | 'danger';
  borderColor: string;
  titleKey: string;
  descriptionKey: string;
  volatilityLabelKey: string;
  volatilityValueKey: string;
}

/**
 * Determines the suitability profile type based on the score.
 *
 * @param score - The suitability test score
 * @returns The profile type
 */
export const getSuitabilityProfileType = (
  score: number,
): SuitabilityProfileType => {
  if (score >= 46) return 'aggressive';
  if (score >= 31) return 'dynamic';
  if (score >= 16) return 'moderate';
  return 'conservative';
};

/**
 * Gets the metadata for a suitability profile type.
 *
 * @param profileType - The profile type
 * @returns The profile metadata
 */
export const getSuitabilityProfileMetadata = (
  profileType: SuitabilityProfileType,
): SuitabilityProfileMetadata => {
  switch (profileType) {
    case 'conservative':
      return {
        type: 'conservative',
        icon: 'Shield',
        iconColor: 'main',
        borderColor: '!border-primary/20',
        titleKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.conservative.title',
        descriptionKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.conservative.description',
        volatilityLabelKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.conservative.volatility.label',
        volatilityValueKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.conservative.volatility.value',
      };
    case 'moderate':
      return {
        type: 'moderate',
        icon: 'TrendingUp',
        iconColor: 'success',
        borderColor: '!border-positive/20',
        titleKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.moderate.title',
        descriptionKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.moderate.description',
        volatilityLabelKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.moderate.volatility.label',
        volatilityValueKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.moderate.volatility.value',
      };
    case 'dynamic':
      return {
        type: 'dynamic',
        icon: 'Zap',
        iconColor: 'secondary',
        borderColor: '!border-pending/20',
        titleKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.dynamic.title',
        descriptionKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.dynamic.description',
        volatilityLabelKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.dynamic.volatility.label',
        volatilityValueKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.dynamic.volatility.value',
      };
    case 'aggressive':
      return {
        type: 'aggressive',
        icon: 'Flame',
        iconColor: 'danger',
        borderColor: '!border-negative/20',
        titleKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.aggressive.title',
        descriptionKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.aggressive.description',
        volatilityLabelKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.aggressive.volatility.label',
        volatilityValueKey:
          'onboarding-process-overview.result-and-signature.suitability-test.profiles.aggressive.volatility.value',
      };
  }
};
